import { Plan } from "@/clients/models/Plan";
import { FlameIcon } from "@/components/icons/flame-icon";
import { useGetCalories } from "@/hooks/requests/nutritional-requirement/use-get-calories";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo, useMemo, useState } from "react";
import { getPlanDayEntry } from "../../utils/plan-week-data";
import { CalorieProgressFill } from "./calorie-progress-fill";
import { getMacroBarWidth, getMacroPercents } from "./macro-percent";

interface CaloriesProgressProps {
  day: string;
  selectedDateKey?: string;
  plan?: Plan;
  /**
   * `"v2"` shows the flame glyph next to the daily kcals label (My Plan
   * redesign). `"default"` keeps the legacy bar untouched.
   */
  variant?: "default" | "v2";
}

const formatCalories = (value: number) => {
  return new Intl.NumberFormat("en-US").format(Math.round(value));
};

export const CaloriesProgress = memo(function CaloriesProgress({
  day,
  selectedDateKey,
  plan,
  variant = "default",
}: CaloriesProgressProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { calories } = useGetCalories();
  const selectedPlanDayEntry = useMemo(
    () => getPlanDayEntry(plan, day, selectedDateKey),
    [day, plan, selectedDateKey],
  );
  const planData = selectedPlanDayEntry?.weekPlan.data ?? plan?.plan?.[0]?.data;

  const selectedDayPlan = useMemo(() => {
    if (selectedPlanDayEntry?.dayPlan) {
      return selectedPlanDayEntry.dayPlan;
    }

    return planData?.find((dayPlan) => dayPlan.day.toLowerCase() === day.toLowerCase());
  }, [day, planData, selectedPlanDayEntry]);

  const targetCalories = calories?.dailyCalories ?? 0;

  const totals = useMemo(() => {
    const selectedMeals = selectedDayPlan?.meals;
    const selectedExtraMeals = selectedDayPlan?.extraMeals ?? [];
    const flatMeals = selectedMeals?.flatMap((mealGroup) => mealGroup.data) ?? [];
    // Sum every meal planned for the day. (Previously this only counted meals the
    // user had ticked off as eaten, but the tick-off gesture was removed — with no
    // writer left to set `isTickedOff`, the filter always matched nothing and the
    // totals collapsed to 0.)
    const dayMeals = [...flatMeals, ...selectedExtraMeals];

    const calorieTotal = dayMeals
      .map((meal) => meal.nutritionalInfo.perPortion?.energy?.kcal)
      .filter((kcal): kcal is number => typeof kcal === "number")
      .reduce((acc, curr) => acc + curr, 0);

    const proteinTotal = dayMeals
      .map((meal) => meal.nutritionalInfo.perPortion?.protein?.grams)
      .filter((grams): grams is number => typeof grams === "number")
      .reduce((acc, curr) => acc + curr, 0);

    const carbsTotal = dayMeals
      .map((meal) => meal.nutritionalInfo.perPortion?.carbohydrate?.total?.grams)
      .filter((grams): grams is number => typeof grams === "number")
      .reduce((acc, curr) => acc + curr, 0);

    const fatTotal = dayMeals
      .map((meal) => meal.nutritionalInfo.perPortion?.fat?.total?.grams)
      .filter((grams): grams is number => typeof grams === "number")
      .reduce((acc, curr) => acc + curr, 0);

    return { calories: calorieTotal, protein: proteinTotal, carbs: carbsTotal, fat: fatTotal };
  }, [selectedDayPlan]);

  // Bar fill: the day's planned kcals as a proportion of the user's daily calorie target (capped in the fill component).
  const progressWidth = useMemo(() => {
    if (targetCalories <= 0) {
      return 0;
    }
    return (totals.calories / targetCalories) * 100;
  }, [totals.calories, targetCalories]);

  const macroPercents = useMemo(() => getMacroPercents(totals), [totals]);

  // Calories remaining for the day. Clamped at 0 so an over-target day renders
  // "0 kcal left" rather than a negative number.
  const caloriesLeft = Math.max(targetCalories - totals.calories, 0);

  if (!plan) {
    return (
      <div className="mx-4">
        <IonSkeletonText animated className="h-[93.5px] w-full rounded-2xl" />
      </div>
    );
  }

  // My Plan redesign: a serif "kcal left" hero, the flame/total line, and a
  // single always-visible macro row with coloured dots (no bars, no toggle).
  if (variant === "v2") {
    return (
      <section
        className={cn("relative rounded-[10px] bg-white/30", "mx-4 mt-4 mb-1")}
        aria-label="Daily nutrition summary"
      >
        <div className="flex w-full flex-col px-4 pt-4 pb-4">
          <div className="flex items-baseline gap-2">
            <span className={cn(textVariants.serif, "text-[54px] leading-none text-black")}>
              {formatCalories(caloriesLeft)}
            </span>
            <span className={cn(textVariants.serif, "text-[30px] leading-none text-black")}>
              kcal left
            </span>
          </div>

          <div className="mt-2 flex items-center">
            <FlameIcon className="mr-1.5 h-4 w-[13px] shrink-0 text-black" />
            <p className={cn(textVariants.footnote, "!font-medium")}>
              <strong className="!font-bold">{formatCalories(totals.calories)}</strong> /{" "}
              <strong className="!font-bold">{formatCalories(targetCalories)}</strong> kcals
            </p>
          </div>

          <div className="bg-violet-25 pointer-events-none relative mt-4 h-1 w-full overflow-hidden rounded-full">
            <div
              className="h-full rounded-full bg-[#620ad5] transition-[width] duration-300 ease-out"
              style={{ width: `${Math.min(Math.max(progressWidth, 0), 100)}%` }}
            />
          </div>

          <div className="mt-4 flex items-center justify-between">
            <MacroDot
              label="Protein"
              value={Math.round(totals.protein)}
              percent={macroPercents.protein}
              color="#27bc8a"
            />
            <MacroDot
              label="Carbs"
              value={Math.round(totals.carbs)}
              percent={macroPercents.carbs}
              color="#ffc300"
            />
            <MacroDot
              label="Fat"
              value={Math.round(totals.fat)}
              percent={macroPercents.fat}
              color="#4167ba"
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        "relative rounded-[10px] bg-white/30",
        "shadow-[inset_0_4px_12px_0_rgba(107,63,167,0.14)]",
        "mx-4 mt-5 mb-1",
      )}
      aria-label="Daily nutrition summary"
    >
      <div className="flex w-full flex-col px-4 pt-[21px] pb-[18px]">
        <div className="flex items-center gap-3">
          <h3 className={cn(textVariants.footnote, "!font-bold uppercase")}>Today</h3>

          <div className="pointer-events-none relative h-2 flex-1 overflow-visible rounded-[2px] bg-[rgba(177,187,209,0.5)]">
            <div className="relative h-full">
              <CalorieProgressFill progressWidth={progressWidth} />
            </div>
          </div>
        </div>

        <div className="mt-[17px] flex items-center justify-between">
          <div className="flex items-center">
            <p className={cn(textVariants.footnote, "!font-medium")}>
              <strong className="!font-bold">{formatCalories(totals.calories)}</strong> /{" "}
              <strong className="!font-bold">{formatCalories(targetCalories)}</strong> kcals
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className={cn(
              textVariants.footnote,
              "text-os-brand !font-semibold underline transition-opacity active:opacity-70",
            )}
          >
            {isExpanded ? "Hide macros" : "See macros"}
          </button>
        </div>

        {isExpanded && (
          <div className="mt-4">
            <div className="mb-4 h-px w-full bg-[#d9d9d9] opacity-20" />
            {/* gap-3, not gap-8: with the percentage appended the widest label
                ("P 172g (155%)") needs 94px, and gap-8 only leaves 87px per column. */}
            <div className="flex items-start justify-between gap-3 pb-1">
              <MacroBar
                label="P"
                value={Math.round(totals.protein)}
                percent={macroPercents.protein}
                color="#27bc8a"
              />
              <MacroBar
                label="C"
                value={Math.round(totals.carbs)}
                percent={macroPercents.carbs}
                color="#ffc300"
              />
              <MacroBar
                label="F"
                value={Math.round(totals.fat)}
                percent={macroPercents.fat}
                color="#4167ba"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

interface MacroDotProps {
  /** Full macro name — the compact dot row has room for the whole word. */
  label: string;
  value: number;
  /** Share of the total calories contributed by all three macros. */
  percent: number;
  color: string;
}

function MacroDot({ label, value, percent, color }: MacroDotProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-[5px] w-[5px] shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <p
        className="text-[12px] whitespace-nowrap text-black"
        style={{ fontFamily: "var(--font-sans-serif)" }}
      >
        {label} <strong className="font-bold">{value}g</strong> ({percent}%)
      </p>
    </div>
  );
}

interface MacroBarProps {
  /** Short form only ("P" / "C" / "F") — the full words no longer fit alongside the percentage. */
  label: string;
  value: number;
  /** Share of the total calories contributed by all three macros. */
  percent: number;
  color: string;
}

function MacroBar({ label, value, percent, color }: MacroBarProps) {
  return (
    <div className="flex flex-1 flex-col gap-[13px]">
      <p className={cn(textVariants.bodySmall, "text-center whitespace-nowrap text-black")}>
        <strong>
          {label} {value}g ({percent}%)
        </strong>
      </p>
      <div className="h-[6px] w-full rounded-[8px] bg-[#d7dce7]">
        <div
          className="h-full rounded-[8px] transition-[width] duration-300 ease-out"
          style={{ width: `${getMacroBarWidth(percent)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
